import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { usePermission } from '@/features/permissions/usePermission'
import { crm2Api, useCrm2Meta, useCrm2Settings, useCrm2Write } from './api'
import { ImportPanel } from './ImportPanel'
import { BookingSettingsSection } from './BookingSettings'
import { Crm2Frame, Field, PersonSelect, Section } from './shared'

/** The switch for production, who gets the tech tasks, and the sample companies for the staging preview. */
export function SettingsPage() {
  const { can } = usePermission()
  const settings = useCrm2Settings()
  const products = useCrm2Meta().data?.products ?? []
  const configure = can('manage', 'Crm')
  const save = useCrm2Write(crm2Api.saveSettings, () => 'Saved')
  const seed = useCrm2Write(crm2Api.seed, (r) => (r.created ? `${r.created} sample companies added` : 'The samples are already there'))
  const clear = useCrm2Write(crm2Api.clear, (r) => `${r.removed} sample companies removed`)
  return (
    <Crm2Frame title="Settings" description={settings.data?.production ? "The switch for TapCRMv2 and TapPM, and who gets the tech tasks." : "TapCRMv2 and TapPM are on in staging. Production stays off until it is switched on there, deliberately."}>
      {settings.error ? (
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      ) : settings.isPending ? (
        <LoadingState label="Loading settings" />
      ) : (
        <div className="grid max-w-4xl gap-4">
          <Section title="Switched on">
            <div className="flex items-center gap-3 px-4 py-3 text-sm">
              <Switch checked={settings.data.on} disabled={!configure || !settings.data.production} onCheckedChange={(v) => save.mutate({ enabled: v })} aria-label="Switched on" />
              <span>{settings.data.production ? (settings.data.on ? 'On in production' : 'Off in production') : 'Always on here (staging). The switch only matters in production.'}</span>
            </div>
          </Section>
          <Section title="Website demo requests">
            <div className="grid gap-2 px-4 py-3">
              <Field label="New leads from the taptalent.ai demo form go to" hint="They land in Marketing · New with “Book the demo” due the next working day. Nobody leaves them on the Dashboard’s “no owner” list.">
                <PersonSelect label="Gets new demo requests" value={settings.data.demoOwnerId} onChange={(v) => configure && save.mutate({ demoOwnerId: v })} />
              </Field>
            </div>
          </Section>
          {settings.data.booking ? <BookingSettingsSection key={JSON.stringify(settings.data.booking)} value={settings.data.booking} canEdit={configure} /> : null}
          <Section title="Tech leads">
            <div className="grid gap-4 px-4 py-3">
              <Field label="Default tech lead" hint="Gets the general tech tasks when a project opens at Pre-Pilot, and any product's tasks when that product has no lead of its own.">
                <PersonSelect label="Default tech lead" value={settings.data.techLeadId} onChange={(v) => configure && save.mutate({ techLeadId: v })} />
              </Field>
              <div className="grid gap-2">
                <div className="text-xs font-medium text-muted-foreground">Per product line: gets that product's set-up, configuration and pilot tasks, and joins the project team</div>
                <div className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
                  {products.map((p) => (
                    <label key={p.key} className="grid grid-cols-[8.5rem_minmax(0,1fr)] items-center gap-2 text-sm">
                      <span className="font-medium">{p.label}</span>
                      <PersonSelect label={`${p.label} tech lead`} value={settings.data.productTechLeads?.[p.key] ?? null} onChange={(v) => configure && save.mutate({ productTechLeads: { [p.key]: v } })} />
                    </label>
                  ))}
                </div>
                <p className="text-tiny text-text-muted">“Nobody” uses the default tech lead. Applies to tasks created from now on; existing tasks keep their assignee.</p>
              </div>
            </div>
          </Section>
          {configure ? <ImportPanel canRun={configure} /> : null}
          {!settings.data.production ? (
            <Section title="Sample companies">
              <div className="grid gap-3 px-4 py-3 text-sm">
                <p className="text-muted-foreground">Eleven made-up companies spread over every pipeline (one in each Marketing stage, three in Sales, a won client in Post-sales, a lost one, a partner and a podcast guest), so the layout can be judged with something in it.</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => seed.mutate(undefined)} disabled={!configure || seed.isPending}>Add sample companies</Button>
                  <Button size="sm" variant="outline" onClick={() => clear.mutate(undefined)} disabled={!configure || clear.isPending}>Remove them</Button>
                </div>
              </div>
            </Section>
          ) : null}
        </div>
      )}
    </Crm2Frame>
  )
}
